import { useState } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Copy, Save, Star, Package, Scroll, Coins, Book, Sparkles, Edit } from 'lucide-react'
import type { ItemResult } from '@creanizer/types'
import { ItemEffectsTab, ItemAcquisitionTab, ItemLoreTab } from './ItemResultTabs'
import { getRarityIcon, formatBasicInfo, formatEffects } from './ItemResultUtils'
import { ItemEditModal } from './ItemEditModal'

interface ItemResultProps {
  result: ItemResult
  onSave?: () => void
  isSaved?: boolean
  isBatch?: boolean
  batchIndex?: number
  onEdit?: (updatedResult: Partial<ItemResult>) => void
}

export function ItemResult({ result, onSave, isSaved, isBatch, batchIndex, onEdit }: ItemResultProps) {
  const [copiedTab, setCopiedTab] = useState<string | null>(null)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)

  const copyToClipboard = (text: string, tabName: string) => {
    navigator.clipboard.writeText(text)
    setCopiedTab(tabName)
    setTimeout(() => setCopiedTab(null), 2000)
  }

  // 防御的チェック
  if (!result || !result.basicInfo) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        アイテムデータが利用できません
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {isBatch && (
        <div className="text-sm text-muted-foreground">
          アイテム {batchIndex ? batchIndex + 1 : 1}
        </div>
      )}
      
      <div className="flex items-start justify-between">
        <div>
          <h3 className="text-xl font-bold flex items-center gap-2">
            {getRarityIcon(result.basicInfo.rarity || 'common')}
            {result.basicInfo.name || '名称不明'}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {result.basicInfo.type || '不明'} / {result.basicInfo.category || '不明'}
          </p>
        </div>
        <div className="flex gap-2">
          {onSave && (
            <Button
              variant={isSaved ? 'secondary' : 'outline'}
              size="sm"
              onClick={onSave}
              disabled={isSaved}
            >
              <Save className="h-4 w-4 mr-2" />
              {isSaved ? '保存済み' : '保存'}
            </Button>
          )}
          {onEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditModalOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              編集
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-3 md:grid-cols-5 gap-1">
          <TabsTrigger value="basic" className="text-xs">
            <Package className="h-3 w-3 mr-1" />
            基本
          </TabsTrigger>
          <TabsTrigger value="appearance" className="text-xs">
            <Sparkles className="h-3 w-3 mr-1" />
            外見
          </TabsTrigger>
          <TabsTrigger value="effects" className="text-xs">
            <Star className="h-3 w-3 mr-1" />
            効果
          </TabsTrigger>
          <TabsTrigger value="acquisition" className="text-xs">
            <Coins className="h-3 w-3 mr-1" />
            入手
          </TabsTrigger>
          <TabsTrigger value="lore" className="text-xs">
            <Book className="h-3 w-3 mr-1" />
            伝承
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-4">
          <ItemBasicTab 
            result={result}
            onCopy={() => copyToClipboard(formatBasicInfo(result), 'basic')}
            isCopied={copiedTab === 'basic'}
          />
        </TabsContent>

        <TabsContent value="appearance" className="space-y-4">
          <ItemAppearanceTab appearance={result.appearance} />
        </TabsContent>

        <TabsContent value="effects" className="space-y-4">
          <ItemEffectsTab effects={result.effects} />
        </TabsContent>

        <TabsContent value="acquisition" className="space-y-4">
          <ItemAcquisitionTab acquisition={result.acquisition} />
        </TabsContent>

        <TabsContent value="lore" className="space-y-4">
          <ItemLoreTab lore={result.lore} />
        </TabsContent>
      </Tabs>
      
      {isEditModalOpen && onEdit && (
        <ItemEditModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={(updatedResult) => {
            onEdit(updatedResult)
            setIsEditModalOpen(false)
          }}
          itemResult={result}
          title={`「${result.basicInfo.name}」を編集`}
        />
      )}
    </div>
  )
}

// 基本情報タブ
function ItemBasicTab({ 
  result, 
  onCopy, 
  isCopied 
}: { 
  result: ItemResult
  onCopy: () => void
  isCopied: boolean
}) {
  // 防御的チェック
  if (!result || !result.basicInfo) {
    return (
      <div className="text-muted-foreground">基本情報がありません</div>
    )
  }

  return (
    <>
      <div className="p-4 bg-muted/50 rounded-lg">
        <p className="text-sm">{result.basicInfo.summary || '概要なし'}</p>
      </div>
      <div className="space-y-2">
        <h4 className="font-semibold">詳細説明</h4>
        <p className="text-sm leading-relaxed">{result.basicInfo.description || '説明なし'}</p>
      </div>
      <Button variant="outline" size="sm" onClick={onCopy}>
        <Copy className="h-4 w-4 mr-2" />
        {isCopied ? 'コピー済み' : 'コピー'}
      </Button>
    </>
  )
}

// 外見タブ
function ItemAppearanceTab({ appearance }: { appearance: ItemResult['appearance'] }) {
  // 防御的チェック
  if (!appearance) {
    return (
      <div className="text-muted-foreground">外見情報がありません</div>
    )
  }

  return (
    <>
      <div className="space-y-2">
        <h4 className="font-semibold">外観</h4>
        <p className="text-sm leading-relaxed">{appearance.visualDescription || '詳細不明'}</p>
      </div>
      <div className="grid grid-cols-2 gap-4 text-sm">
        {appearance.material && (
          <div>
            <span className="font-medium">素材:</span> {appearance.material}
          </div>
        )}
        {appearance.size && (
          <div>
            <span className="font-medium">サイズ:</span> {appearance.size}
          </div>
        )}
        {appearance.weight && (
          <div>
            <span className="font-medium">重量:</span> {appearance.weight}
          </div>
        )}
        {appearance.color && (
          <div>
            <span className="font-medium">色:</span> {appearance.color}
          </div>
        )}
      </div>
      {appearance.distinctiveFeatures && appearance.distinctiveFeatures.length > 0 && (
        <div>
          <h4 className="font-semibold mb-2">特徴</h4>
          <ul className="text-sm space-y-1">
            {appearance.distinctiveFeatures.map((feature, idx) => (
              <li key={idx}>・{feature}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  )
}